import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { delay, map, startWith } from 'rxjs';
import { FormErrorsModule } from '@shared/components/form-elements/form-errors/form-errors.module';
import { InputFieldComponent } from '@shared/components/form-elements/input-field/input-field.component';
import { LocalLoaderComponent } from '@shared/components/loader/local-loader/local-loader.component';
import { AuthService } from '@core/authentication/services/auth.service';
import { ForgotPasswordService } from '@b2b/features/forgot-password-dialog/core/forgot-password.service';
import * as ForgotPasswordValidators from '@b2b/features/forgot-password-dialog/core/forgot-password.validators';

@Component({
  selector: 'app-new-password',
  imports: [
    CommonModule,
    TranslateModule,
    ReactiveFormsModule,
    FormsModule,
    FormErrorsModule,
    LocalLoaderComponent,
    InputFieldComponent,
  ],
  templateUrl: './new-password.component.html',
  styleUrl: './new-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NewPasswordComponent implements OnInit {
  minLength = 6;
  maxLength = 20;

  form = new FormGroup(
    {
      oldPassword: new FormControl(''),
      newPassword: new FormControl('', [
        Validators.required,
        Validators.minLength(this.minLength),
        Validators.maxLength(this.maxLength),
        ForgotPasswordValidators.alphanumericValidator,
        ForgotPasswordValidators.specialCharacterValidator,
        ForgotPasswordValidators.sequentialValidator,
      ]),
      passwordConfirm: new FormControl('', [Validators.required]),
    },
    {
      validators: ForgotPasswordValidators.matchFieldsValidator('newPassword', 'passwordConfirm'),
    },
  );

  validations$ = this.form.valueChanges.pipe(
    startWith(this.form.value),
    delay(100),
    map(() => {
      const errors = {
        ...this.form.get('newPassword')?.errors,
        ...this.form?.errors,
      };

      return [
        {
          name: 'forgotPassword.newPassword.validationMaxLength',
          isValid: !errors?.['maxlength'],
          isDirty: this.form.get('newPassword')?.dirty,
        },
        {
          name: 'forgotPassword.newPassword.validationMinLength',
          isValid: !errors?.['minlength'],
          isDirty: this.form.get('newPassword')?.dirty,
        },
        {
          name: 'forgotPassword.newPassword.validationAlphanumeric',
          isValid: !errors?.['alphanumeric'],
          isDirty: this.form.get('newPassword')?.dirty,
        },
        {
          name: 'forgotPassword.newPassword.validationSpecialCharacter',
          isValid: !errors?.['specialCharacter'],
          isDirty: this.form.get('newPassword')?.dirty,
        },
        {
          name: 'forgotPassword.newPassword.validationSequence',
          isValid: !errors?.['sequential'],
          isDirty: this.form.get('newPassword')?.dirty,
        },
        {
          name: 'forgotPassword.newPassword.validationEqual',
          isValid: !errors?.['fieldsMismatch'],
          isDirty: this.form.get('passwordConfirm')?.dirty,
        },
      ];
    }),
  );

  isLoading$ = this.forgotPasswordService.isLoading$;
  error$ = this.forgotPasswordService.error$;
  title$ = this.forgotPasswordService.newPasswordTitle$;

  isAuthenticated!: boolean;

  constructor(
    private authService: AuthService,
    private forgotPasswordService: ForgotPasswordService,
  ) {}

  ngOnInit() {
    this.isAuthenticated = this.authService.isAuthenticated();
    if (this.isAuthenticated) {
      this.form.get('oldPassword')?.setValidators([Validators.required]);
    } else {
      this.form.get('oldPassword')?.clearValidators();
    }
    this.form.get('oldPassword')?.updateValueAndValidity();
  }

  submit() {
    if (this.form.valid) {
      this.forgotPasswordService.sendNewPassword(this.form.value.newPassword!, this.form.value.oldPassword!);
    }
  }
}
